import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { enableAnalytics, disableAnalytics } from '../analyticsLoader';

const STORAGE_KEY = 'naijanest_cookie_consent';

// Bump this if the categories or their meaning change. Stored consent from
// an older version is treated as no decision, so visitors are re-prompted.
const CONSENT_VERSION = 1;

// A decision more than this old is treated as stale and re-prompted, per
// regulator guidance (e.g. France's CNIL recommends ~6 months) that consent
// shouldn't be assumed valid indefinitely.
const CONSENT_MAX_AGE_MS = 180 * 24 * 60 * 60 * 1000;

const DEFAULT_CATEGORIES = { functional: false, analytics: false, marketing: false };

function readStoredConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) {
      return null;
    }
    const decidedAt = Date.parse(parsed.decidedAt);
    if (!Number.isFinite(decidedAt) || Date.now() - decidedAt > CONSENT_MAX_AGE_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

const CookieConsentContext = createContext(null);

export function CookieConsentProvider({ children }) {
  const [consent, setConsent] = useState(readStoredConsent);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const persist = useCallback((categories) => {
    const record = {
      necessary: true,
      functional: Boolean(categories.functional),
      analytics: Boolean(categories.analytics),
      marketing: Boolean(categories.marketing),
      version: CONSENT_VERSION,
      decidedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    } catch {
      // Storage unavailable (private mode, quota) - consent still applies
      // for this session via React state, just won't survive a reload.
    }
    setConsent(record);
  }, []);

  const acceptAll = useCallback(
    () => persist({ functional: true, analytics: true, marketing: true }),
    [persist],
  );

  const rejectNonEssential = useCallback(() => persist(DEFAULT_CATEGORIES), [persist]);

  const savePreferences = useCallback(
    (categories) =>
      persist({
        functional: Boolean(categories.functional),
        analytics: Boolean(categories.analytics),
        marketing: Boolean(categories.marketing),
      }),
    [persist],
  );

  const openSettings = useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

  // Apply the analytics scripts' on/off state immediately whenever consent
  // changes, and on first load for a returning visitor who already decided.
  useEffect(() => {
    if (consent?.analytics) {
      enableAnalytics();
    } else {
      disableAnalytics();
    }
  }, [consent?.analytics]);

  const value = useMemo(
    () => ({
      consent,
      hasDecided: consent !== null,
      isSettingsOpen,
      acceptAll,
      rejectNonEssential,
      savePreferences,
      openSettings,
      closeSettings,
    }),
    [consent, isSettingsOpen, acceptAll, rejectNonEssential, savePreferences, openSettings, closeSettings],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider');
  }
  return ctx;
}
